<template>
  <AppDialogContent
    v-model="dialog"
    :fullscreen="$vuetify.display.smAndDown"
    :tile="$vuetify.display.smAndDown"
    :persistent="checkoutInProcess"
    v-bind="$attrs"
    data-test="showLicenceDialog"
    width="785px"
    class="px-2"
    no-parent-css-actions
  >
    <template #header>
      <div ref="dialog" />
      {{
        $t(
          "horses:horseProfilePage.HorseLicence.header",
          "Purchase licence for a horse",
          "Header on horse profile"
        )
      }}
    </template>

    <v-row no-gutters class="mb-2 px-2">
      <v-col cols="12" md="8">
        <div class="bg-strong elevation-1 py-2 px-3">
          <h3 class="mt-2">
            {{
              $t(
                "events:common.Horse.label",
                "Horse",
                "Label for singular horse in different parts of the system"
              )
            }}
          </h3>
          <AppShowHorseWithDetails :horse="horse" />
        </div>
      </v-col>
    </v-row>

    <table class="entsys--table mt-3">
      <thead>
        <tr />
      </thead>
      <tbody>
        <tr v-if="canBuyTemporaryLicence">
          <td>
            <b>
              {{
                $t(
                  "horses:horseProfilePage.HorseTemporaryLicence.Modal.product.label"
                )
              }}
            </b>
          </td>

          <td class="text-center" style="width: 15%">
            <span
              class="text-accent bold"
              style="white-space: nowrap; margin-right: 7px"
            >
              {{ masterCurrency(licenceProducts.TEMPORARY.priceCents) }}
            </span>
          </td>
        </tr>
        <tr v-if="canBuyLifetimeLicense">
          <td>
            <v-row dense>
              <div v-if="canBuyMultiple">
                <v-checkbox
                  v-model="selectedProducts"
                  multiple
                  class="mt-0"
                  hide-details
                  data-test="lifetime"
                  value="LIFETIME"
                  :label="
                    $t(
                      'horses:horseProfilePage.licencePayment.horseLifetimeSportLicenceProduct.label',
                      'Lifetime licence'
                    )
                  "
                />
              </div>
              <div v-else class="pl-3 my-auto">
                <b>
                  {{
                    $t(
                      "horses:horseProfilePage.licencePayment.horseLifetimeSportLicenceProduct.label",
                      "Lifetime licence"
                    )
                  }}
                </b>
              </div>
            </v-row>
          </td>

          <td class="text-center" style="width: 15%">
            <span
              class="text-accent bold"
              style="white-space: nowrap; margin-right: 7px"
            >
              {{ masterCurrency(licenceProducts.LIFETIME.priceCents) }}
            </span>
          </td>
        </tr>

        <tr v-if="canBuyFeiLicence">
          <td>
            <v-row dense>
              <div v-if="canBuyMultiple">
                <v-checkbox
                  v-model="selectedProducts"
                  multiple
                  class="mt-0"
                  hide-details
                  data-test="fei"
                  value="FEI"
                  :label="
                    $t(
                      'horses:horseProfilePage.FEILicencePayment.Modal.label',
                      'Fei Licence'
                    )
                  "
                />
              </div>
              <div v-else class="pl-3 my-auto">
                <b>
                  {{
                    $t(
                      "horses:horseProfilePage.FEILicencePayment.Modal.label",
                      "Fei Licence"
                    )
                  }}
                </b>
              </div>
            </v-row>
          </td>

          <td class="text-center" style="width: 15%">
            <span
              class="text-accent bold"
              style="white-space: nowrap; margin-right: 7px"
            >
              {{ masterCurrency(licenceProducts.FEI.priceCents) }}
            </span>
          </td>
        </tr>

        <tr v-if="canBuySeasonLicence">
          <td>
            <v-row dense>
              <div v-if="canBuyMultiple">
                <v-checkbox
                  v-model="selectedProducts"
                  multiple
                  hide-details
                  class="mt-0"
                  data-test="season"
                  value="SEASON"
                  :label="
                    $t(
                      'horses:horseProfilePage.licencePayment.Modal.label',
                      'Horse season licence',
                      'Label for season licence selection when buying horse licences'
                    )
                  "
                />
              </div>
              <div v-else class="pl-3 my-auto">
                <b>
                  {{
                    $t(
                      "horses:horseProfilePage.licencePayment.Modal.label",
                      "Horse season licence",
                      "Label for season licence selection when buying horse licences"
                    )
                  }}
                </b>
              </div>
            </v-row>
          </td>

          <td class="text-center" style="width: 15%">
            <span
              class="text-accent bold"
              style="white-space: nowrap; margin-right: 7px"
            >
              {{ masterCurrency(licenceProducts.SEASON.priceCents) }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="pa-4">
      <div v-if="selectedFeiLicence">
        <AppSelectDiscipline
          v-model="form.disciplineId"
          exclude-non-fei
          data-test="disciplineId"
          @click="v$.form.disciplineId.$touch()"
        />
      </div>

      <div v-if="canBuyTemporaryLicence">
        <div class="pa-2">
          <b>{{
            $t(
              "horses:horseProfilePage.HorseTemporaryLicence.Modal.product.hint"
            )
          }}</b>
        </div>
        <v-row dense>
          <v-col>
            <AppDatePicker
              v-model="form.licenceValidFrom"
              :min="minFrom"
              :max="maxFrom"
              data-test="validFrom"
              :label="
                $t(
                  'horses:horseProfilePage.HorseTemporaryLicence.Modal.validFrom.label'
                )
              "
              :hint="
                $t(
                  'horses:horseProfilePage.HorseTemporaryLicence.Modal.validFrom.hint'
                )
              "
              persistent-hint
            />
          </v-col>
          <v-col class="pl-4">
            <b>{{
              $t(
                "horses:horseProfilePage.HorseTemporaryLicence.Modal.validUntil.label"
              )
            }}</b>
            <br />
            <template v-if="form.licenceValidFrom">
              {{
                $dayjs.date(
                  $dayjs(form.licenceValidFrom).add(
                    masterConfiguration.horseTemporaryLicenceDefaultLength,
                    "days"
                  )
                )
              }}
            </template>
          </v-col>
        </v-row>
      </div>

      <div v-if="!!seasonFEIYearSelect" data-test="seasonInputWrapper">
        <div>
          <b>{{
            $t(
              "horses:horseProfilePage.licencePayment.FEIlicence.season.label",
              "Select season"
            )
          }}</b>
        </div>

        <template v-if="seasonFEIYearSelect.possibleValues.length == 0">
          {{
            $t(
              "horses:horseProfilePage.licencePayment.FEIlicence.seasonSelection.error.message",
              "FEI registration for selected discipline is already activated."
            )
          }}
        </template>
        <template v-else-if="seasonFEIYearSelect.possibleValues.length == 1">
          {{ seasonFEIYearSelect.possibleValues[0].text }}
        </template>
        <template v-else>
          <v-radio-group
            v-model="form.year"
            :error-messages="fieldErrorMessages('year')"
            :disabled="fieldDisabled('year')"
            @click="v$.form.year.$touch()"
          >
            <v-radio
              v-for="possibleValue in seasonFEIYearSelect.possibleValues"
              :key="possibleValue.value"
              :value="possibleValue.value"
            >
              <template #label>
                {{ possibleValue.text }}
              </template>
            </v-radio>
          </v-radio-group>
          <div class="text-muted">
            {{
              $t(
                "horses:horseProfilePage.licencePayment.FEIlicence.season.hint",
                "FEI registration requires selection of season. Before 1st of November, only current year can be selected. From 1st of November next year is also available."
              )
            }}
          </div>
        </template>
      </div>

      <div v-if="selectedFEILicenceEndurance">
        <v-textarea
          v-model="form.applicantComment"
          :error-messages="fieldErrorMessages('applicantComment')"
          :disabled="fieldDisabled('applicantComment')"
          :label="
            $t(
              `horses:horseProfilePage.FEILicencePayment.applicantComment.label`,
              'Additional information'
            )
          "
          :hint="
            $t(
              `horses:horseProfilePage.FEILicencePayment.applicantComment.hint`,
              'Please provide additional information for the FEI license application as required. For example trainer name in case of endurance licence.'
            )
          "
          variant="outlined"
          persistent-hint
          data-test="applicantComment"
          class="mt-4"
          @click="v$.form.applicantComment.$touch()"
        />
      </div>
    </div>
    <div class="text-right">
      <v-card color="strong" class="my-2 px-4 py-4 text-end">
        <div style="font-size: 18px; font-weight: bold">
          {{
            $t(
              "horses:horseProfilePage.licencePaymentTotal.modal.label",
              "Total",
              "licence payment modal label - total"
            )
          }}
          <span class="text-accent ml-1 currency-display">{{
            selectedProductsTotalPrice
          }}</span>
        </div>
      </v-card>
    </div>

    <template #actions>
      <v-row dense style="justify-content: flex-end">
        <div
          :class="[
            $vuetify.display.mdAndUp ? 'px-3' : 'px-2 v-col-12 full-width-btn '
          ]"
        >
          <v-btn
            block
            variant="outlined"
            :disabled="checkoutInProcess || initFormLoading"
            class="entsys--btn--round"
            selected-class
            min-width="145px!important"
            @click="dialog = false"
          >
            {{ $t("common:button.back", "Back", "Back button") }}
          </v-btn>
        </div>
        <div
          :class="[
            $vuetify.display.mdAndUp ? 'px-3' : 'px-2 v-col-12 full-width-btn '
          ]"
        >
          <v-btn
            block
            :disabled="cannotCheckout"
            :loading="checkoutInProcess || initFormLoading"
            class="entsys--btn--round"
            color="accent"
            min-width="145px!important"
            data-test="buyLicenceBtn"
            @click="checkout(true)"
          >
            {{
              $t(
                "common:modal.button.ConfirmAddAnother.label",
                "Confirm and add another"
              )
            }}
          </v-btn>
        </div>
        <div
          :class="[
            $vuetify.display.mdAndUp ? 'px-3' : 'px-2 v-col-12 full-width-btn '
          ]"
        >
          <v-btn
            id="submitButton"
            block
            min-width="145px!important"
            :disabled="cannotCheckout"
            :loading="checkoutInProcess || initFormLoading"
            class="entsys--btn--round"
            data-test="checkoutLicence"
            color="accent"
            @click="checkout()"
          >
            {{ $t("common:modal.button.CheckOut.label", "Check-out") }}
          </v-btn>
        </div>
      </v-row>
      <p class="text-muted mt-2 bold text-left">
        {{
          $t(
            "horses:horseProfilePage.licencePayment.modal.description",
            "When pressing Check-out button you will be redirected to shopping basket. You can add more horse licences to shopping basket and pay them all together as one payment",
            "Results tab heading on horse profile"
          )
        }}
      </p>
    </template>
  </AppDialogContent>
</template>
<script>
import { defineComponent, ref } from "vue";
import gql from "fraql";
import { useCurrency } from "@composables/masterdata/config";
import { getFederationConfiguration } from "@composables/masterdata/config";
import { useSystemProductHelper } from "@composables/masterdata/products/systemProducts";
import { useDisciplineHelper } from "@composables/masterdata/competitions/disciplines";
import AppDatePicker from "@elements/AppDatePicker.vue";
import AppShowHorseWithDetails from "@AppShows/horse/AppShowHorseWithDetails.vue";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppSelectDiscipline from "@AppSelects/AppSelectDiscipline.vue";
import FipaListener from "@/utils/listenerUtility";
import { useFormValidation } from "@/composables/form/form_validation";

export default defineComponent({
  name: "HorseLicencePurchasesForm",
  components: {
    AppDialogContent,
    AppShowHorseWithDetails,
    AppDatePicker,
    AppSelectDiscipline
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    horse: {
      type: Object,
      required: true
    },
    disableRedirectToHorseSearch: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "add-another"],
  setup() {
    const { getSystemProductFromMaster } = useSystemProductHelper();

    const form = ref({
      horseId: null,
      licenceValidFrom: null,
      disciplineId: null,
      applicantComment: null,
      year: null
    });
    const initHorseApplicationFeiLicenceForm = ref({
      formFields: [],
      vuelidateValidations: {},
      readOnlyFields: []
    });

    const { v$, fieldDisabled, fieldErrorMessages, legacyFormErrorMessages } =
      useFormValidation(initHorseApplicationFeiLicenceForm, form);

    return {
      ...getFederationConfiguration(),
      ...useCurrency(),
      ...useDisciplineHelper(),

      getSystemProductFromMaster,
      v$,
      fieldDisabled,
      fieldErrorMessages,
      legacyFormErrorMessages,
      initHorseApplicationFeiLicenceForm,
      form
    };
  },
  data() {
    return {
      checkoutInProcess: false,
      selectedProducts: []
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      }
    },
    seasonFEIYearSelect() {
      const formFields = this.initHorseApplicationFeiLicenceForm.formFields;
      return formFields.find(x => x.name == "year");
    },
    selectedFEILicenceEndurance() {
      if (!this.selectedFeiLicence || !this.form.disciplineId) return false;
      if (
        this.getDisciplineFromMaster(this.form.disciplineId)?.name ==
        "endurance"
      )
        return true;
      return false;
    },
    initFormLoading() {
      return this.$apollo.queries.initHorseApplicationFeiLicenceForm.loading;
    },
    canBuyTemporaryLicence() {
      return this.$allowedMutation(
        this.horse.allowedMutations,
        "horsePurchaseTemporarySportLicence"
      );
    },
    canBuySeasonLicence() {
      return this.$allowedMutation(
        this.horse.allowedMutations,
        "horsePurchaseSeasonSportLicence"
      );
    },
    canBuyFeiLicence() {
      return this.$allowedMutation(
        this.horse.allowedMutations,
        "horseApplicationFeiSeasonLicence"
      );
    },
    canBuyLifetimeLicense() {
      return this.$allowedMutation(
        this.horse.allowedMutations,
        "horsePurchaseLifetimeSportLicence"
      );
    },
    canBuyMultiple() {
      return (
        (this.canBuySeasonLicence || this.canBuyLifetimeLicense) &&
        this.canBuyFeiLicence
      );
    },
    minFrom() {
      return this.$dayjs().subtract(1, "year").format("YYYY-MM-DD");
    },
    maxFrom() {
      return this.$dayjs().add(1, "year").format("YYYY-MM-DD");
    },
    cannotCheckout() {
      if (this.checkoutInProcess) {
        return true;
      }
      if (this.canBuyMultiple && this.selectedProducts.length == 0) {
        return true;
      }
      if (this.selectedFeiLicence) {
        return (
          this.form.disciplineId == null ||
          (this.seasonFEIYearSelect?.possibleValues?.length ?? 0) == 0
        );
      }

      if (this.canBuyTemporaryLicence && !this.form.licenceValidFrom) {
        return true;
      }
      return false;
    },
    useMutationName() {
      if (this.canBuyTemporaryLicence)
        return "horsePurchaseTemporarySportLicence";
      if (this.selectedFeiLicence) return "horseApplicationFeiSeasonLicence";
      if (this.selectedLifetimeLicense)
        return "horsePurchaseLifetimeSportLicence";
      return "horsePurchaseSeasonSportLicence";
    },
    licenceProducts() {
      return {
        SEASON:
          this.getSystemProductFromMaster("HORSE_SEASON_SPORT_LICENCE") || {},
        TEMPORARY:
          this.getSystemProductFromMaster("HORSE_TEMPORARY_SPORT_LICENCE") ||
          {},
        FEI: this.getSystemProductFromMaster("HORSE_SEASON_FEI_LICENCE") || {},
        LIFETIME:
          this.getSystemProductFromMaster("HORSE_LIFETIME_SPORT_LICENCE") || {}
      };
    },
    selectedTemporaryLicence() {
      return (
        this.selectedProducts != null &&
        this.selectedProducts.includes("TEMPORARY")
      );
    },
    selectedSeasonLicence() {
      return (
        this.selectedProducts != null &&
        this.selectedProducts.includes("SEASON")
      );
    },
    selectedFeiLicence() {
      return (
        this.selectedProducts != null && this.selectedProducts.includes("FEI")
      );
    },
    selectedLifetimeLicense() {
      return (
        this.selectedProducts != null &&
        this.selectedProducts.includes("LIFETIME")
      );
    },
    selectedProductsTotalPrice() {
      let totalPriceCents = 0;
      if (this.selectedTemporaryLicence)
        totalPriceCents += this.licenceProducts.TEMPORARY.priceCents;
      if (this.selectedSeasonLicence)
        totalPriceCents += this.licenceProducts.SEASON.priceCents;
      if (this.selectedFeiLicence)
        totalPriceCents += this.licenceProducts.FEI.priceCents;
      if (this.selectedLifetimeLicense)
        totalPriceCents += this.licenceProducts.LIFETIME.priceCents;

      return this.masterCurrency(totalPriceCents);
    }
  },
  // Theses are used for parent component setting this's components selectedProducts
  // Used in HorseShop for better UX by not having to click twice on same intended product
  productCodenames: {
    HORSE_SEASON_FEI_LICENCE: "FEI",
    HORSE_SEASON_SPORT_LICENCE: "SEASON",
    HORSE_LIFETIME_SPORT_LICENCE: "LIFETIME"
  },
  watch: {
    selectedProducts(nv) {
      if (!nv.includes("FEI")) {
        // Clear initForm so it wont interfere with other products
        this.initHorseApplicationFeiLicenceForm = {
          formFields: [],
          vuelidateValidations: {},
          readOnlyFields: []
        };
      }
    },
    dialog(v) {
      if (v == true) {
        this.$refs.dialog?.scrollIntoView();
      }
    }
  },
  beforeMount() {
    this.form.horseId = this.horse.id;

    if (this.canBuyMultiple) this.selectedProducts = [];
    else if (this.canBuyLifetimeLicense) this.selectedProducts = ["LIFETIME"];
    else if (this.canBuyTemporaryLicence) this.selectedProducts = ["TEMPORARY"];
    else if (this.canBuySeasonLicence) this.selectedProducts = ["SEASON"];
    else if (this.canBuyFeiLicence) this.selectedProducts = ["FEI"];
  },
  apollo: {
    // Only run this query if FEI season licence is selected
    // And when dialog is opened
    initHorseApplicationFeiLicenceForm: {
      query: gql`
        query initHorseApplicationFeiLicenceForm(
          $horseId: ID
          $disciplineId: ID
        ) {
          initHorseApplicationFeiLicenceForm(
            horseId: $horseId
            disciplineId: $disciplineId
          ) {
            formFields {
              name
              possibleValues
              inputType
            }
            vuelidateValidations
            readOnlyFields
          }
        }
      `,
      skip() {
        return !this.dialog || !this.selectedProducts.includes("FEI");
      },
      variables() {
        return {
          horseId: this.horse.id,
          disciplineId: this.form.disciplineId
        };
      },
      manual: true,
      result({ loading, data }) {
        if (loading) return;
        this.initHorseApplicationFeiLicenceForm =
          data.initHorseApplicationFeiLicenceForm;
        const formFields = data.initHorseApplicationFeiLicenceForm.formFields;
        const yearInput = formFields.find(x => x.name == "year");
        if (yearInput && yearInput.possibleValues[0]) {
          this.form.year = yearInput.possibleValues[0].value;
        }
      }
    }
  },
  methods: {
    checkout(redirectHorseSearch) {
      if (this.useMutationName === "horseApplicationFeiSeasonLicence") {
        this.v$.form.year.$touch();
        this.v$.form.applicantComment.$touch();
        this.v$.form.disciplineId.$touch();
        const err = this.v$.form;
        if (
          err.year.$invalid ||
          err.applicantComment.$invalid ||
          err.disciplineId.$invalid
        ) {
          return;
        }
      }

      const typicalReturn = `
        payable {
          id
          personId
          shoppingBasketId
        }
      `;
      const FEIReturn = `
        payables {
          id
          personId
        }
        shoppingBasketId
      `;

      this.checkoutInProcess = true;

      this.$apollo
        .mutate({
          mutation: gql`
            mutation ${this.useMutationName}(
              $horseId: ID!
              $shoppingBasketId: ID
              ${
                this.useMutationName === "horseApplicationFeiSeasonLicence"
                  ? "$applicantComment: String"
                  : ""
              }
              ${
                this.useMutationName === "horsePurchaseTemporarySportLicence"
                  ? "$validFrom: Date"
                  : ""
              }
              ${
                this.useMutationName === "horseApplicationFeiSeasonLicence"
                  ? "$disciplineId: ID!, $year: Int"
                  : ""
              }
              ${
                this.useMutationName === "horseApplicationFeiSeasonLicence"
                  ? "$includeSeasonLicence: Boolean"
                  : ""
              }
            ) {
              ${this.useMutationName}(
                input: {
                  horseId: $horseId
                  shoppingBasketId: $shoppingBasketId
                   ${
                     this.useMutationName === "horseApplicationFeiSeasonLicence"
                       ? "applicantComment: $applicantComment"
                       : ""
                   }

                  ${
                    this.useMutationName ===
                    "horsePurchaseTemporarySportLicence"
                      ? "validFrom: $validFrom"
                      : ""
                  }
                  ${
                    this.useMutationName === "horseApplicationFeiSeasonLicence"
                      ? "disciplineId: $disciplineId, year: $year"
                      : ""
                  }
                  ${
                    this.useMutationName === "horseApplicationFeiSeasonLicence"
                      ? "includeSeasonLicence: $includeSeasonLicence"
                      : ""
                  }
                }
              ) {
               ${
                 this.useMutationName === "horseApplicationFeiSeasonLicence"
                   ? FEIReturn
                   : typicalReturn
               }
              }
            }
          `,
          variables: {
            horseId: this.horse.id,
            shoppingBasketId: sessionStorage.getItem("shoppingBasketId"),
            validFrom: this.form.licenceValidFrom,
            applicantComment: this.form.applicantComment,
            disciplineId: this.form.disciplineId,
            year: this.form.year,
            includeSeasonLicence:
              this.useMutationName === "horseApplicationFeiSeasonLicence" &&
              this.selectedSeasonLicence
                ? true
                : false
          }
        })
        .then(({ data }) => {
          const returned = data[this.useMutationName];
          const shoppingBasketId = returned.payable
            ? returned.payable.shoppingBasketId
            : returned.shoppingBasketId
              ? returned.shoppingBasketId
              : null;
          const personId =
            returned.payable && returned.payable.personId
              ? returned.payable.personId
              : returned.payables && returned.payables[0]
                ? returned.payables[0].personId
                : null;
          if (
            !personId &&
            !sessionStorage.getItem("shoppingBasketId") &&
            shoppingBasketId
          ) {
            sessionStorage.setItem("shoppingBasketId", shoppingBasketId);
          }

          FipaListener.emit("emitRefreshBasket");

          if (!this.disableRedirectToHorseSearch && redirectHorseSearch) {
            this.$router.push({ name: "HorsesSearchPage" });
            return;
          }

          if (this.disableRedirectToHorseSearch && redirectHorseSearch) {
            this.$emit("add-another");
            this.checkoutInProcess = false;
            return;
          }

          if (personId) {
            this.$router.push({
              name: "Payables",
              params: { personId: personId }
            });
            return;
          }
          this.$router.push({
            name: "AnonymouseBasket",
            params: {
              shoppingBasketId: shoppingBasketId
            }
          });
        })
        .catch(err => {
          this.checkoutInProcess = false;
          this.$root.$snackbarServerError(err, { level: "error" });
        });
    }
  },
  fragments: {
    horseForRegistrationFields: gql`
      fragment _ on HorseForRegistration {
        id
        allowedMutations
      }
    `
  }
});
</script>
